import { baseToApiShape, baseFromApiShape, baseInitNewForm, baseInitEditForm, withHeadOnlyPlaceholder } from '../formHelpers/base';

const EFA_INSTANCE_TYPES = [
    "m5dn.24xlarge", "m5dn.metal", "m5n.24xlarge", "m5n.metal", "m5zn.12xlarge", "m5zn.metal", "m6a.48xlarge", "m6a.metal", "m6i.32xlarge",
    "m6i.metal", "m6id.32xlarge", "m6id.metal", "m6idn.32xlarge", "m6idn.metal", "m6in.32xlarge", "m6in.metal", "m7g.16xlarge", "m7g.metal",
    "c5n.9xlarge", "c5n.18xlarge", "c5n.metal", "c6a.48xlarge", "c6a.metal", "c6gn.16xlarge", "c6i.32xlarge", "c6i.metal", "c6id.32xlarge",
    "c6id.metal", "c6in.32xlarge", "c6in.metal", "c7g.16xlarge", "c7g.metal", "c7gn.16xlarge", "hpc6a.48xlarge", "hpc7g.4xlarge", "hpc7g.8xlarge",
    "hpc7g.16xlarge", "hpc6id.32xlarge", "r5dn.24xlarge", "r5dn.metal", "r5n.24xlarge", "r5n.metal", "r6a.48xlarge", "r6a.metal", "r6i.32xlarge",
    "r6i.metal", "r6idn.32xlarge", "r6idn.metal", "r6in.32xlarge", "r6in.metal", "r6id.32xlarge", "r6id.metal", "r7g.16xlarge", "r7g.metal",
    "x2idn.32xlarge", "x2idn.metal", "x2iedn.32xlarge", "x2iedn.metal", "x2iezn.12xlarge", "x2iezn.metal", "i3en.12xlarge", "i3en.24xlarge",
    "i3en.metal", "i4g.16xlarge", "i4i.32xlarge", "i4i.metal", "im4gn.16xlarge", "dl1.24xlarge", "g4dn.8xlarge", "g4dn.12xlarge", "g4dn.16xlarge",
    "g4dn.metal", "g5.8xlarge", "g5.12xlarge", "g5.16xlarge", "g5.24xlarge", "g5.48xlarge", "g6.8xlarge", "g6.12xlarge", "g6.16xlarge",
    "g6.24xlarge", "g6.48xlarge", "g6e.8xlarge", "g6e.12xlarge", "g6e.16xlarge", "g6e.24xlarge", "g6e.48xlarge", "inf1.24xlarge", "p3dn.24xlarge",
    "p4d.24xlarge", "p4de.24xlarge", "p5.48xlarge", "trn1.32xlarge", "trn1n.32xlarge", "vt1.24xlarge", "c7gn.12xlarge",
];

/**
 * AWS-specific flat → nested API payload.
 * Extends base with: capacity auto-expire, marketplace AMIs, storage configurations, prefix.
 */
export function toApiShape(form) {
    const base = baseToApiShape(form);

    // Ensure EFA disabled for incompatible instances
    const nodeGroups = (form.nodeGroups || []).map(g => ({
        ...g,
        efa: isEfaCompatible(g.instance_id) ? (g.efa || false) : false,
    }));

    return {
        ...base,
        info: {
            ...base.info,
            prefix: form.prefix || '',
            marketplace_head_image_id: form.marketplaceHeadImageId || null,
            marketplace_compute_image_id: form.marketplaceComputeImageId || null,
        },
        data: {
            ...base.data,
            storageConfigurations: form.storageConfigurations || [],
            network: {
                ...base.data.network,
                zone_name: form.zoneName || '',
                capacity_reservation_auto_expire: form.capacityReservationAutoExpire ?? true,
                capacity_reservation_expire_hours: form.capacityReservationExpireHours ?? 1,
            },
            node: {
                ...base.data.node,
                group: withHeadOnlyPlaceholder(nodeGroups, form),
            },
        },
    };
}

export function fromApiShape(cluster) {
    return {
        ...baseFromApiShape(cluster),

        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,

        zoneName: cluster.data?.network?.zone_name || '',
        capacityReservationAutoExpire: cluster.data?.network?.capacity_reservation_auto_expire ?? true,
        capacityReservationExpireHours: cluster.data?.network?.capacity_reservation_expire_hours ?? 1,

        storageConfigurations: cluster.data?.storageConfigurations || [],
    };
}

export function initNewForm(provider, config) {
    return {
        ...baseInitNewForm(provider, config),
        capacityReservationAutoExpire: true,
        capacityReservationExpireHours: 1,
        storageConfigurations: [],
        marketplaceHeadImageId: null,
        marketplaceComputeImageId: null,
    };
}

export function initEditForm(cluster, config) {
    return {
        ...baseInitEditForm(cluster, config),

        prefix: cluster.info?.prefix || '',
        marketplaceHeadImageId: cluster.info?.marketplace_head_image_id || null,
        marketplaceComputeImageId: cluster.info?.marketplace_compute_image_id || null,
        zoneName: cluster.data?.network?.zone_name || '',
        capacityReservationAutoExpire: cluster.data?.network?.capacity_reservation_auto_expire ?? true,
        capacityReservationExpireHours: cluster.data?.network?.capacity_reservation_expire_hours ?? 1,
        storageConfigurations: cluster.data?.storageConfigurations || [],
    };
}

/** Check if instance supports EFA — provider data first, then fallback list */
export function isEfaCompatible(instanceId, instanceSizes) {
    if (instanceSizes) {
        const inst = instanceSizes.find(s => s.instance_id === instanceId);
        if (inst?.EFA_support) return true;
    }
    return EFA_INSTANCE_TYPES.includes(instanceId);
}

/** Check if any node groups are in different zones (EFA requires same zone) */
export function areZonesDifferent(form) {
    const headZone = form?.zoneId;
    const nodeZones = (form?.nodeGroups || []).map(g => g.zone_id || headZone).filter(Boolean);
    if (headZone) nodeZones.push(headZone);
    return new Set(nodeZones).size > 1;
}
